import React, { ReactNode, useEffect, useRef, useState } from "react";
import FilterSection from "./FilterSection";
import Tabs from "./Tabs";
import reportApi from "../reports/ReportApi";
import { useUser } from "src/context/UserContext";
import { getLast7DaysRange } from "src/util/Util";
import { useDateRange } from "src/context/DateRangeContext";
import Loading from "../ui/Loading";
import toast from "react-hot-toast";
import Toast from "../ui/Toast";
import { useLocation } from "react-router";

interface DashboardLayoutProps {
  children: ReactNode;
}

const DomainFilterLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const location = useLocation();
  const isReportPage = location.pathname === "/report";
  const { user } = useUser();
  const { dateRange,setDateRange, isFilterOpen,setIsFilterOpen, selectedDomains, selectedMobileAppIds, setDashboardReportData, hasManualDomainSelection, hasManualMobileAppIdSelection, setHasManualDomainSelection, setHasManualMobileAppIdSelection, setSelectedDomains, setSelectedMobileAppIds, topN } =
    useDateRange();
  const [loading, setLoading] = useState(false);
  const initialRange = getLast7DaysRange();
  const [domainList, setDomainList] = useState<any[]>([]);
  const [mobileAppIdList, setMobileAppIdList] = useState<any[]>([]);
  const [lastAppliedDateRange, setLastAppliedDateRange] = useState<{fromDate: string, toDate: string} | null>(null);
  
  const tabs = [
    {
      href: "/dashboard",
      name: "Dashboard",
      icon: "dashboardIcon",
    },
    {
      href: "/report",
      name: "Report",
      icon: "reporticon",
    },
  ];


  const isInitialMount = useRef(true);
  const isTopNInitialMount = useRef(true);

  useEffect(() => {
    if (loading) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [loading]);

  useEffect(() => {
    loadInitialData();
    setDateRange({
      fromDate: initialRange?.fromDate,
      toDate: initialRange?.toDate,
    });
  }, []);

  // Reload data when switching between Dashboard and Report routes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    loadData();
  }, [location.pathname]);

  // Reload data when topN changes (from ReportsDashboard dropdown)
  useEffect(() => {
    if (isTopNInitialMount.current) {
      isTopNInitialMount.current = false;
      return;
    }
    if (isReportPage) {
      loadData();
    }
  }, [topN]);
  const loadData = async (domainsToUse?: any[], mobileAppIdsToUse?: any[]) => {
    setLoading(true);
    const initialRange = getLast7DaysRange();
    const domains = domainsToUse !== undefined ? domainsToUse : selectedDomains;
    const mobileAppIds = mobileAppIdsToUse !== undefined ? mobileAppIdsToUse : selectedMobileAppIds;

    const domainsArray = Array.isArray(domains) ? domains : [];
    const mobileAppIdsArray = Array.isArray(mobileAppIds) ? mobileAppIds : [];

    const domainsToSend = domainsArray?.length === 0
      ? []
      : domainsArray?.length === domainList?.length
        ? ["all"]
        : domainsArray;

    const mobileAppIdsToSend = mobileAppIdsArray?.length === 0
      ? []
      : mobileAppIdsArray?.length === mobileAppIdList?.length
        ? ["all"]
        : mobileAppIdsArray;

    let uuid: string | undefined;

    if (isReportPage) {
      const resp = await reportApi
        .createTopNPublisherReport({
          publisherId: user?.publisherId,
          fromDate: dateRange?.fromDate || initialRange.fromDate,
          toDate: dateRange?.toDate || initialRange.toDate,
          domains: domainsToSend,
          mobileAppIds: mobileAppIdsToSend,
          topN,
          orderByColumn: "Revenue ($)",
          orderDirection: "desc",
          // limitByColumn: "Domain",
        });
      uuid = resp?.data;
    } else {
      uuid = await reportApi
        .createPublisherDashboard({
          publisherId: user?.publisherId,
          fromDate: dateRange?.fromDate || initialRange.fromDate,
          toDate: dateRange?.toDate || initialRange.toDate,
          domains: domainsToSend,
          mobileAppIds: mobileAppIdsToSend,
        })
        .then((resp: any) => resp.data);
    }

    if (!uuid) {
      setLoading(false);
      return;
    }

    let interval = setInterval(async () => {
      try {
        const response = await reportApi.get(uuid ?? "");
        clearInterval(interval);
        const jsonx = await response.text();

        let lines = jsonx.split("\n");
        lines = lines.filter(line => {
          const parts = line.split(",");
          return parts[0].trim() !== "";
        });

        const cleaned = lines.join("\n");
        setDashboardReportData(cleaned);

        setLoading(false);
      } catch (response) {
        if (typeof response === "object") {
          if (response.status !== 425) {
            setLoading(false);
            clearInterval(interval);
          }
        }
      }
    }, 500);
  };

  const initialLoadData = async (domains: any[], mobileAppIds: any[]) => {
    setLoading(true);
    const initialRange = getLast7DaysRange();

    const domainsArray = Array.isArray(domains) ? domains : [];
    const mobileAppIdsArray = Array.isArray(mobileAppIds) ? mobileAppIds : [];

    const domainsToSend = domainsArray?.length > 0 ? ["all"] : [];
    const mobileAppIdsToSend = mobileAppIdsArray?.length > 0 ? ["all"] : [];

    let uuid: string | undefined;

    if (isReportPage) {
      const resp = await reportApi
        .createTopNPublisherReport({
          publisherId: user?.publisherId,
          fromDate: dateRange?.fromDate || initialRange.fromDate,
          toDate: dateRange?.toDate || initialRange.toDate,
          domains: domainsToSend,
          mobileAppIds: mobileAppIdsToSend,
          topN,
          orderByColumn: "Revenue ($)",
          orderDirection: "desc",
          // limitByColumn: "Domain",
        });
      uuid = resp?.data;
    } else {
      uuid = await reportApi
        .createPublisherDashboard({
          publisherId: user?.publisherId,
          fromDate: dateRange?.fromDate || initialRange.fromDate,
          toDate: dateRange?.toDate || initialRange.toDate,
          domains: domainsToSend,
          mobileAppIds: mobileAppIdsToSend,
        })
        .then((resp: any) => resp.data);
    }

    if (!uuid) {
      setLoading(false);
      return;
    }

    let interval = setInterval(async () => {
      try {
        const response = await reportApi.get(uuid ?? "");
        clearInterval(interval);
        const jsonx = await response.text();

        let lines = jsonx.split("\n");
        lines = lines.filter(line => {
          const parts = line.split(",");
          return parts[0].trim() !== "";
        });

        const cleaned = lines.join("\n");
        setDashboardReportData(cleaned);
        setLoading(false);
      } catch (response: any) {
        if (typeof response === "object") {
          if (response.status !== 425) {
            setLoading(false);
            clearInterval(interval);
          }
        }
      }
    }, 500);
  };

  const loadDomainData = async () => {
    const data: any = await reportApi
      .getPublisherDomains({
        publisherId: user?.publisherId,
        fromDate: dateRange?.fromDate,
        toDate: dateRange?.toDate,
      })
      .then((resp) => resp);
    
    const domainArray = Array.isArray(data) ? data : [];
    if (domainArray?.length) {
      setDomainList(domainArray);
    }
    return domainArray;
  };

  const loadMobileAppIdData = async () => {
    const data: any = await reportApi
      .getPublisherMobileAppIds({
        publisherId: user?.publisherId,
        fromDate: dateRange?.fromDate,
        toDate: dateRange?.toDate,
      })
      .then((resp) => resp);
    
    const mobileAppIdArray = Array.isArray(data) ? data : [];
    if (mobileAppIdArray?.length) {
      setMobileAppIdList(mobileAppIdArray);
    }
    return mobileAppIdArray;
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [domains, mobileAppIds] = await Promise.all([
        loadDomainData(),
        loadMobileAppIdData()
      ]);
      
      const domainsArray = Array.isArray(domains) ? domains : [];
      const mobileAppIdsArray = Array.isArray(mobileAppIds) ? mobileAppIds : [];
      
      setSelectedDomains(domainsArray);
      setSelectedMobileAppIds(mobileAppIdsArray);
      await initialLoadData(domainsArray, mobileAppIdsArray);

      setLastAppliedDateRange({
        fromDate: dateRange?.fromDate || initialRange.fromDate,
        toDate: dateRange?.toDate || initialRange.toDate
      });
    } catch (error) {
      console.error("Error loading initial data:", error);
      toast.custom(
        (t) => (
          <Toast
            message="Error loading initial data. Please try again."
            type="error"
          />
        ),
        { position: "bottom-left", duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    const domainsArray = Array.isArray(selectedDomains) ? selectedDomains : [];
    const mobileAppIdsArray = Array.isArray(selectedMobileAppIds) ? selectedMobileAppIds : [];
    
    if (domainsArray?.length !== domainList?.length && domainsArray?.length > 100) {
      toast.custom(
        (t) => (
          <Toast
            message="Select up to 100 domains at a time, or select all domains."
            type="error"
          />
        ),
        { position: "bottom-left", duration: 2000 }
      );
      return false;
    }
    
    if (mobileAppIdsArray?.length !== mobileAppIdList?.length && mobileAppIdsArray?.length > 100) {
      toast.custom(
        (t) => (
          <Toast
            message="Select up to 100 mobile app IDs at a time, or select all mobile app IDs."
            type="error"
          />
        ),
        { position: "bottom-left", duration: 2000 }
      );
      return false;
    }
    
    setIsFilterOpen(false);
    setLoading(true);
    
    try {
      // Always refresh domains and mobile app IDs when Apply is clicked
      const [domains, mobileAppIds] = await Promise.all([
        loadDomainData(),
        loadMobileAppIdData()
      ]);

      // Check if date range has changed
      const dateRangeChanged = !lastAppliedDateRange ||
        lastAppliedDateRange.fromDate !== dateRange?.fromDate ||
        lastAppliedDateRange.toDate !== dateRange?.toDate;

      if (dateRangeChanged) {
        // Auto-select all domains
        setSelectedDomains(domains || []);

        // Keep previously selected mobile app IDs if they still exist in the new list
        const mobileAppIdsList = mobileAppIds || [];
        const filteredMobileAppIds = selectedMobileAppIds.filter((id: string) =>
          mobileAppIdsList.includes(id)
        );
        setSelectedMobileAppIds(filteredMobileAppIds);

        // Load data with all domains and filtered mobile app IDs
        await loadData(domains || [], filteredMobileAppIds);

        setSelectedDomains(domainsArray);
        setSelectedMobileAppIds(mobileAppIdsArray);
        setHasManualDomainSelection(false);
        setHasManualMobileAppIdSelection(false);

        await loadData(domainsArray, mobileAppIdsArray);
      } else {
        await loadData(domainsArray, mobileAppIdsArray);
      }

      setLastAppliedDateRange({
        fromDate: dateRange?.fromDate,
        toDate: dateRange?.toDate
      });
    } catch (error) {
      console.error("Error in handleApply:", error);
      toast.custom(
        (t) => (
          <Toast
            message="Error applying filters. Please try again."
            type="error"
          />
        ),
        { position: "bottom-left", duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex text-white">
      {loading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0B0B0BCC]">
          <Loading type="block" />
        </div>
      )}

      <aside
        className={` sm:w-[40%] md:w-[35%] lg:w-[25%] 2xl:w-[25%] sm:min-w-[250px] shrink-0 h-[calc(100vh-56px)] sticky top-[56px] overflow-y-auto overflow-x-visible z-20 ${
          isFilterOpen ? "absolute w-[98%]" : "hidden"
          } sm:block pl-[14px] sm:pl-[30px] md:pl-[40px]`}
      >
        <FilterSection
          selectedTimePeriod={initialRange}
          domains={domainList}
          mobileAppIds={mobileAppIdList}
          onApply={handleApply}
        />
      </aside>

      <main className="flex-1 min-w-0 py-6 pl-6 pr-6 sm:pr-[40px]">
        <div className="sticky top-[56px] z-10 bg-[#0A0A0A] pb-2">
          <div className="flex items-center justify-between">
            <Tabs tabs={tabs} />
          </div>
        </div>
        <div>{children}</div>
      </main>
    </div>
  );
};

export default DomainFilterLayout;